import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Check stock layout
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The physical geometry of the blank check stock, in inches.
 *
 * Deliberately SYSTEM-WIDE rather than per company or per bank account: one
 * printed sheet carries checks drawn on several different banks, so every face
 * on it must share the same paper geometry. A per-account stock setting could
 * not be honoured by a mixed batch, which is the whole point of the queue.
 *
 * Only the numbers here have to match the paper. Everything printed *inside* a
 * check face is positioned as a fraction of that face, so changing the width or
 * height re-flows the payee line, amount box and signature block automatically —
 * on blank stock nothing is pre-printed, so those positions are ours to choose.
 * The two exceptions are the MICR offsets, which are physical standards measured
 * from the check's own edges and must not be scaled.
 */
export const checkStockLayoutSchema = z.object({
  pageWidthIn: z.number().min(3).max(17).default(8.5),
  pageHeightIn: z.number().min(3).max(17).default(11),

  checksPerSheet: z.number().int().min(1).max(4).default(3),

  /** Width of one check face — NOT the sheet, when the check sits on one side. */
  checkWidthIn: z.number().min(2).max(17).default(6),
  checkHeightIn: z.number().min(1.5).max(8).default(2.81),

  /**
   * Distance from the sheet's left edge to the check's left edge. Non-zero when
   * the checks sit on the right of the stock with a tearoff column beside them.
   */
  checkLeftIn: z.number().min(0).max(14).default(2.5),
  /** Distance from the sheet's top edge to the FIRST check's top edge. */
  firstCheckTopIn: z.number().min(0).max(14).default(0),
  /**
   * Top-of-check to top-of-next-check. Usually equals checkHeightIn; larger when
   * the stock leaves a gap or a tear strip between faces.
   */
  checkPitchIn: z.number().min(1.5).max(8).default(2.81),

  /** Print payment details on the stub beside the check instead of leaving it blank. */
  stubEnabled: z.boolean().default(true),

  /**
   * MICR placement, measured from the CHECK's own bottom and right edges — the
   * check is the document that goes through the reader once it is torn off.
   * ANSI X9.100-160 puts the baseline 0.25" up (±1/16") and position 1 at the
   * right edge; our line stops at position 13 so the bank can encode the amount
   * in positions 1–12. Exposed because a bank occasionally asks for a tweak, not
   * because they are a matter of taste.
   */
  micrBaselineFromBottomIn: z.number().min(0.1).max(0.7).default(0.25),
  micrRightMarginIn: z.number().min(0.5).max(4).default(1.625),

  /** Scales every text size on the face at once, for a small or crowded check. */
  fontScale: z.number().min(0.5).max(2).default(1),
});
export type CheckStockLayout = z.infer<typeof checkStockLayoutSchema>;

/**
 * Defaults taken from Check Depot CDW162 wallet-format blank stock: an 8.5"×11"
 * sheet carrying three 6"×2.81" checks with tearoffs at the bottom and sides.
 * With the checks on the right, the side tearoff is the 2.5" column to their
 * left, and 3 × 2.81" = 8.43" leaves a ~2.57" tearoff across the bottom.
 *
 * These come off a product page, not off a ruler. The vertical origin in
 * particular is a guess: the checks may start flush at the top or sit below a
 * margin. Print the calibration sheet, lay it on real stock, and correct these
 * before running a live batch.
 */
export const DEFAULT_CHECK_STOCK_LAYOUT: CheckStockLayout = checkStockLayoutSchema.parse({});

export interface CheckStockLayoutStatus {
  layout: CheckStockLayout;
  /** False while the defaults are still in place and nothing has been measured. */
  configured: boolean;
  updatedAt: string | null;
  /** Geometry problems that would put ink off the paper. Empty when it fits. */
  problems: string[];
}

/**
 * Check the geometry closes before anything reaches a printer. These are the
 * mistakes that waste a whole sheet of check stock: a face that runs off the
 * page, faces that overlap each other, or a MICR line with no room to sit in.
 */
export function validateCheckStockLayout(l: CheckStockLayout): string[] {
  const problems: string[] = [];
  const round = (n: number) => Math.round(n * 1000) / 1000;

  if (l.checkLeftIn + l.checkWidthIn > l.pageWidthIn + 0.001) {
    problems.push(
      `The check runs off the right edge: ${round(l.checkLeftIn)}" from the left + ${round(l.checkWidthIn)}" wide = ${round(l.checkLeftIn + l.checkWidthIn)}", but the sheet is only ${round(l.pageWidthIn)}" wide.`,
    );
  }

  const lastBottom = l.firstCheckTopIn + (l.checksPerSheet - 1) * l.checkPitchIn + l.checkHeightIn;
  if (lastBottom > l.pageHeightIn + 0.001) {
    problems.push(
      `${l.checksPerSheet} checks need ${round(lastBottom)}" of height, but the sheet is only ${round(l.pageHeightIn)}" tall.`,
    );
  }

  if (l.checkPitchIn + 0.001 < l.checkHeightIn) {
    problems.push(
      `The spacing between checks (${round(l.checkPitchIn)}") is less than a check is tall (${round(l.checkHeightIn)}"), so the faces would overlap.`,
    );
  }

  if (l.micrRightMarginIn + 1 > l.checkWidthIn) {
    problems.push(
      `A ${round(l.checkWidthIn)}" check leaves no room for the MICR line once ${round(l.micrRightMarginIn)}" is reserved for the bank's amount field.`,
    );
  }

  if (l.micrBaselineFromBottomIn > l.checkHeightIn / 3) {
    problems.push('The MICR baseline sits too far up the check to be read reliably.');
  }

  return problems;
}

// ─────────────────────────────────────────────────────────────────────────────
// Bank account check-writing setup
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ABA routing transit number check digit, per the Federal Reserve's E-Payments
 * routing directory: 9 digits, weighted 3-7-1 repeating, sum must be 0 mod 10.
 *
 * This is worth validating on the way in rather than discovering at the bank:
 * a transposed digit produces stock that no reader will accept, and by then the
 * checks are printed, signed, and in the mail.
 */
export function isValidRoutingNumber(value: string): boolean {
  if (!/^\d{9}$/.test(value)) return false;
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(value[i]) * w[i]!;
  return sum % 10 === 0;
}

const routingNumber = z
  .string()
  .trim()
  .regex(/^\d{9}$/, 'routing number must be exactly 9 digits')
  .refine(isValidRoutingNumber, 'routing number failed its ABA check digit — re-read it off a real check');

const accountNumber = z
  .string()
  .trim()
  .regex(/^\d{4,17}$/, 'account number must be 4–17 digits');

export const checkSetupInputSchema = z.object({
  /**
   * Omit it. Whether an account writes checks is the "Writes checks" toggle on
   * the Chart of Accounts; this screen only edits the printing details. Accepted
   * here so an API caller can flip it without a second call.
   */
  checkWritingEnabled: z.boolean().optional(),
  routingNumber: routingNumber.nullable().optional(),
  accountNumber: accountNumber.nullable().optional(),
  bankName: z.string().trim().max(120).nullable().optional(),
  bankAddress: z.string().trim().max(300).nullable().optional(),
  fractionalRouting: z.string().trim().max(40).nullable().optional(),
  /** Next number the printer will use. Cannot move backwards past a used number. */
  nextCheckNumber: z.number().int().positive().max(99_999_999),
  signatureName: z.string().trim().max(120).nullable().optional(),
  offsetXPt: z.number().min(-72).max(72).default(0),
  offsetYPt: z.number().min(-72).max(72).default(0),
});
export type CheckSetupInput = z.infer<typeof checkSetupInputSchema>;

/**
 * What the API hands back. Note the deliberate asymmetry with the input: the
 * routing number returns masked and the account number returns as a last-4 only.
 * Together they authorize an ACH debit, so they are write-only over the wire —
 * once set, they exist to be printed server-side, not read back.
 */
export interface CheckSetup {
  bankAccountId: string;
  companyId: string;
  companyName: string;
  /** GL cash account this stock draws on. */
  accountId: string;
  accountCode: string;
  accountName: string;
  checkWritingEnabled: boolean;
  routingNumberMasked: string | null;
  accountNumberLast4: string | null;
  bankName: string | null;
  bankAddress: string | null;
  fractionalRouting: string | null;
  nextCheckNumber: number;
  hasSignatureImage: boolean;
  signatureName: string | null;
  offsetXPt: string;
  offsetYPt: string;
  /** False until routing, account number, and a signature image are all present. */
  readyToPrint: boolean;
  /** Human list of what is still missing, for the setup screen. */
  missing: string[];
}

export const signatureUploadInputSchema = z.object({
  /** base64 PNG, no data: prefix. */
  imageBase64: z.string().min(1),
  fileName: z.string().trim().max(200),
});
export type SignatureUploadInput = z.infer<typeof signatureUploadInputSchema>;

/**
 * Which ASCII characters carry E-13B's four symbols.
 *
 * The digits are always 0-9, but the transit / amount / on-us / dash symbols sit
 * on different codepoints depending on who made the font — and getting it wrong
 * prints a legible letter where a magnetic symbol belongs. The check looks fine
 * and no bank can read it.
 */
export const micrSymbolsSchema = z.object({
  transit: z.string().length(1),
  amount: z.string().length(1),
  onUs: z.string().length(1),
  dash: z.string().length(1),
});
export type MicrSymbols = z.infer<typeof micrSymbolsSchema>;

/**
 * The two mappings in circulation. Named after the fonts they came from so the
 * setup screen can say something more useful than "A/B/C/D".
 *
 * abcd is the default because it is what "MICR Encoding" (micrenc.ttf) uses —
 * the most widely distributed E-13B face.
 */
export const MICR_SYMBOL_PRESETS = {
  abcd: {
    label: 'A / B / C / D — MICR Encoding (micrenc.ttf) and most freeware fonts',
    symbols: { transit: 'A', amount: 'B', onUs: 'C', dash: 'D' } as MicrSymbols,
  },
  toad: {
    label: 'T / O / A / D — IDAutomation, GnuMICR',
    symbols: { transit: 'T', amount: 'A', onUs: 'O', dash: 'D' } as MicrSymbols,
  },
} as const;

export const DEFAULT_MICR_SYMBOLS: MicrSymbols = MICR_SYMBOL_PRESETS.abcd.symbols;

export const micrFontUploadInputSchema = z.object({
  /** base64 TTF of a licensed E-13B font. */
  fontBase64: z.string().min(1),
  fileName: z.string().trim().max(200),
  /** Defaults to the A/B/C/D mapping; changeable afterwards without re-uploading. */
  symbols: micrSymbolsSchema.optional(),
});
export type MicrFontUploadInput = z.infer<typeof micrFontUploadInputSchema>;

export interface MicrFontStatus {
  installed: boolean;
  fileName: string | null;
  sha256: string | null;
  uploadedAt: string | null;
  sizeBytes: number | null;
  /** Null when no font is installed. */
  symbols: MicrSymbols | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// The cross-company print queue
// ─────────────────────────────────────────────────────────────────────────────

/** One queued check, carrying enough company context to render a mixed batch. */
export interface PrintQueueItem {
  checkId: string;
  companyId: string;
  companyName: string;
  bankAccountId: string;
  bankAccountCode: string;
  bankAccountName: string;
  checkDate: string;
  payeeName: string;
  amount: string;
  memo: string | null;
  /** The number this check WOULD get if printed now. Advisory only — the run reassigns. */
  projectedCheckNumber: string;
  /** False when the drawing account is not fully set up; such checks cannot print. */
  readyToPrint: boolean;
  blockedReason: string | null;
}

export interface PrintQueue {
  items: PrintQueueItem[];
  /** Grand totals across every company, which is the point of batching. */
  totalCount: number;
  readyCount: number;
  blockedCount: number;
  totalAmount: string;
  /** Sheets the ready checks would consume at checksPerSheet. */
  sheetCount: number;
  checksPerSheet: number;
  /** Wasted faces on the final sheet — shown so the owner can decide to wait. */
  wastedFaces: number;
}

export const printChecksInputSchema = z.object({
  /** Explicit allowlist. Omit to print every ready check in the queue. */
  checkIds: z.array(z.string().uuid()).min(1).max(500).optional(),
  /**
   * Omit to use however many faces the configured stock holds. Set lower only to
   * deliberately print a short sheet — the extra faces come out blank.
   */
  checksPerSheet: z.number().int().min(1).max(4).optional(),
});
export type PrintChecksInput = z.infer<typeof printChecksInputSchema>;

export interface PrintRunCheck {
  checkId: string;
  companyId: string;
  companyName: string;
  checkNumber: string;
  payeeName: string;
  amount: string;
  /** 1-based position in the PDF, so a jam can be described as "sheet 2, position 1". */
  sheetIndex: number;
  positionOnSheet: number;
}

export interface PrintRun {
  id: string;
  status: 'pending' | 'confirmed' | 'abandoned';
  checkCount: number;
  sheetCount: number;
  checksPerSheet: number;
  totalAmount: string;
  createdAt: string;
  confirmedAt: string | null;
  notes: string | null;
  checks: PrintRunCheck[];
}

/**
 * The post-print confirmation. Anything the owner does NOT list as spoiled is
 * released as an outstanding check; spoiled numbers are recorded as consumed and
 * their checks go back to the queue to be renumbered on the next run.
 */
export const confirmPrintRunInputSchema = z.object({
  spoiledCheckIds: z.array(z.string().uuid()).default([]),
  spoilReason: z.string().trim().max(500).optional(),
  notes: z.string().trim().max(500).optional(),
});
export type ConfirmPrintRunInput = z.infer<typeof confirmPrintRunInputSchema>;

export interface SpoiledCheckNumber {
  id: string;
  companyId: string;
  companyName: string;
  bankAccountId: string;
  checkNumber: string;
  reason: string;
  printRunId: string | null;
  createdAt: string;
}
